import {
  initialNzengoMemberships,
  initialNzengoTransfers,
  nzengoMockGroups,
  nzengoMockMembers,
  NZENGO_MEMBERSHIP_STATUSES,
  NZENGO_TRANSFER_STATUSES,
} from "./nzengoTransferMockData";

const STORAGE_KEY = "savvy:nzengo-transfer-prototype";

const clone = (value) => JSON.parse(JSON.stringify(value));

const buildDefaultState = () => ({
  groups: clone(nzengoMockGroups),
  members: clone(nzengoMockMembers),
  memberships: clone(initialNzengoMemberships),
  transfers: clone(initialNzengoTransfers),
  notifications: [
    {
      id: "nzengo-seed-request",
      type: "NZENGO_TRANSFER_REQUESTED",
      message: "New transfer request from Amina Mbarak",
      created_at: "2026-09-10T09:30:00.000Z",
      is_read: false,
      audience_role: "Mwenyekiti",
    },
  ],
});

const readState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : buildDefaultState();
  } catch (_error) {
    return buildDefaultState();
  }
};

const writeState = (state) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  return state;
};

const enrichTransfer = (state, transfer) => {
  const member = state.members.find((item) => item.id === transfer.memberId);
  const sourceGroup = state.groups.find((item) => item.id === transfer.sourceGroupId);
  const destinationGroup = state.groups.find((item) => item.id === transfer.destinationGroupId);
  return {
    ...transfer,
    member,
    sourceGroup,
    destinationGroup,
  };
};

export const resetNzengoPrototypeState = () => {
  writeState(buildDefaultState());
};

export const getNzengoCurrentContext = (user = {}, scenario = "eligible") => {
  const state = readState();
  const member = state.members.find((item) => item.id === 501) || state.members[0];
  const currentGroup =
    state.groups.find((group) => group.name === user?.group_name) ||
    state.groups.find((group) => group.id === 101);

  return {
    member,
    currentGroup,
    membership: state.memberships.find(
      (item) => item.memberId === member.id && item.groupId === currentGroup?.id
    ),
    outstandingAmount: scenario === "blocked" ? 25000 : 0,
  };
};

export const getAvailableDestinationNzengos = ({ region, district, currentGroupId }) => {
  const state = readState();
  return state.groups.filter((group) => {
    if (!group.is_active || group.group_category !== "NZENGO") return false;
    if (group.id === currentGroupId) return false;
    if (region && group.region !== region) return false;
    if (district && group.district !== district) return false;
    return true;
  });
};

export const submitNzengoTransferRequest = ({ memberId, sourceGroupId, destinationGroupId, outstandingAmount = 0 }) => {
  const state = readState();
  if (Number(outstandingAmount) > 0) {
    throw new Error("Transfer cannot continue while obligations are outstanding.");
  }
  const destinationGroup = state.groups.find((group) => group.id === destinationGroupId);
  if (!destinationGroup || destinationGroup.group_category !== "NZENGO" || !destinationGroup.is_active) {
    throw new Error("Select an active destination Nzengo.");
  }
  if (Number(sourceGroupId) === Number(destinationGroupId)) {
    throw new Error("Destination Nzengo cannot be the current Nzengo.");
  }

  const transfer = {
    id: Date.now(),
    memberId,
    sourceGroupId,
    destinationGroupId,
    outstandingAmount: Number(outstandingAmount),
    status: NZENGO_TRANSFER_STATUSES.PENDING_APPROVAL,
    requestedAt: new Date().toISOString(),
  };

  state.transfers.unshift(transfer);
  const member = state.members.find((item) => item.id === memberId);
  state.notifications.unshift({
    id: `nzengo-request-${transfer.id}`,
    type: "NZENGO_TRANSFER_REQUESTED",
    message: `New transfer request from ${member?.fullName || "a member"}`,
    created_at: transfer.requestedAt,
    is_read: false,
    audience_role: "Mwenyekiti",
  });
  writeState(state);
  return enrichTransfer(state, transfer);
};

export const getPendingNzengoTransferRequests = () => {
  const state = readState();
  return state.transfers
    .filter((transfer) => transfer.status === NZENGO_TRANSFER_STATUSES.PENDING_APPROVAL)
    .map((transfer) => enrichTransfer(state, transfer));
};

export const approveNzengoTransfer = (transferId) => {
  const state = readState();
  const transfer = state.transfers.find((item) => Number(item.id) === Number(transferId));
  if (!transfer) throw new Error("Transfer request was not found.");

  transfer.status = NZENGO_TRANSFER_STATUSES.APPROVED;
  transfer.approvedAt = new Date().toISOString();

  state.memberships = state.memberships.map((membership) =>
    membership.memberId === transfer.memberId && membership.groupId === transfer.sourceGroupId
      ? { ...membership, status: NZENGO_MEMBERSHIP_STATUSES.TRANSFERRED }
      : membership
  );

  const existingDestination = state.memberships.find(
    (membership) => membership.memberId === transfer.memberId && membership.groupId === transfer.destinationGroupId
  );
  if (existingDestination) {
    existingDestination.status = NZENGO_MEMBERSHIP_STATUSES.ACTIVE;
  } else {
    state.memberships.push({
      id: Date.now() + 1,
      memberId: transfer.memberId,
      groupId: transfer.destinationGroupId,
      status: NZENGO_MEMBERSHIP_STATUSES.ACTIVE,
    });
  }

  const enriched = enrichTransfer(state, transfer);
  state.notifications.unshift(
    {
      id: `nzengo-approved-${transfer.id}`,
      type: "NZENGO_TRANSFER_APPROVED",
      message: `Your transfer to ${enriched.destinationGroup?.name} has been approved`,
      created_at: transfer.approvedAt,
      is_read: false,
      audience_role: "Mwanachama",
    },
    {
      id: `nzengo-joined-${transfer.id}`,
      type: "NZENGO_MEMBER_JOINED",
      message: `${enriched.member?.fullName} has joined ${enriched.destinationGroup?.name}`,
      created_at: transfer.approvedAt,
      is_read: false,
      audience_role: "Mwanachama",
    }
  );
  writeState(state);
  return enrichTransfer(state, transfer);
};

export const rejectNzengoTransfer = (transferId) => {
  const state = readState();
  const transfer = state.transfers.find((item) => Number(item.id) === Number(transferId));
  if (!transfer) throw new Error("Transfer request was not found.");
  transfer.status = NZENGO_TRANSFER_STATUSES.REJECTED;
  transfer.rejectedAt = new Date().toISOString();

  const enriched = enrichTransfer(state, transfer);
  state.notifications.unshift({
    id: `nzengo-rejected-${transfer.id}`,
    type: "NZENGO_TRANSFER_REJECTED",
    message: `Your transfer to ${enriched.destinationGroup?.name} was rejected`,
    created_at: transfer.rejectedAt,
    is_read: false,
    audience_role: "Mwanachama",
  });
  writeState(state);
  return enrichTransfer(state, transfer);
};

export const getNzengoMemberships = () => clone(readState().memberships);

export const getActiveNzengoMemberships = () =>
  getNzengoMemberships().filter((membership) => membership.status === NZENGO_MEMBERSHIP_STATUSES.ACTIVE);

export const getTransferredNzengoMemberships = () =>
  getNzengoMemberships().filter((membership) => membership.status === NZENGO_MEMBERSHIP_STATUSES.TRANSFERRED);

export const getNzengoMockNotifications = () => clone(readState().notifications);
